import {Button, Group, Paper, Stack, Text} from '@mantine/core';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {FieldPath, SubmitHandler, useFormContext} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {CodesService} from '../../api/generated/CodesService';
import {ReviewStatuses} from '../../api/generated/enums';
import {FilesService} from '../../api/generated/FilesService';
import {Error} from '../../api/generated/index.defs';
import {ReviewsService} from '../../api/generated/ReviewsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {BasicPage} from '../../components/basic-page';
import {DeleteButton} from '../../components/delete-button';
import {BreadcrumbPart} from '../../components/page-title';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {useOptions} from '../../hooks/use-options';
import {ROUTES} from '../../routes';
import {
  DocumentCreate,
  DocumentUpdate,
} from '../documents/document-create-update';
import {ReviewDocumentListing} from '../documents/review-document-listing';
import {ReviewSchema, reviewSchema} from './schemas';
import {useJobStore} from '../job-store';
import {extractFileName} from '../../forms/adapters/file-button';

type ReviewProps = {
  isResponse?: boolean;
  isReadonly?: boolean;
  isUpdate?: boolean;
  errors?: Error[];
  jobId?: number;
};

const Form = createForm<ReviewSchema>();

export const ReviewCreate: FC<ReviewProps> = ({isResponse}) => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {jobId} = useTypedParams(ROUTES.ADMIN.JOB_REVIEWS.CREATE);

  const [errors, setErrors] = useState<Error[]>([]);

  const reviewDefaultValues: ReviewSchema = useMemo(
    () => ({
      jobId: jobId,
      message: '',
      status: isResponse
        ? ReviewStatuses['Review Completed']
        : ReviewStatuses['Pending Review'],
      createDocumentRequest: {
        jobId: jobId,
        codeId: 0,
        reviewId: 0,
        name: '',
        url: '',
      },
    }),
    [isResponse, jobId],
  );

  const breadcrumbs: BreadcrumbPart[] = useMemo(
    () => [
      {title: 'Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
      {
        title: `${jobId}`,
        href: ROUTES.ADMIN.JOBS.DETAIL.buildPath({id: jobId}),
      },
      {
        title: 'Reviews',
        href: ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId}),
      },
      {title: 'Create'},
    ],
    [jobId],
  );

  const onSubmit = useCallback<SubmitHandler<ReviewSchema>>(
    async (value) => {
      if (value.createDocumentRequest && !value.createDocumentRequest.name)
        value.createDocumentRequest.name = extractFileName(
          value.createDocumentRequest.url,
        );
      const response = await ReviewsService.create({
        body: value,
      });

      if (response.hasErrors) {
        setErrors(response.errors);
        return response;
      }

      notifications.success('Review Created');
      navigate(ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId}));
    },
    [jobId, navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        defaultValues={reviewDefaultValues}
        schema={reviewSchema}
        onSubmit={onSubmit}
      >
        {({formState: {isSubmitting}}) => (
          <Form.Container>
            <FormFields isResponse={isResponse} errors={errors} jobId={jobId} />
            <Group mt={24} justify={'flex-end'}>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId})}
              >
                Cancel
              </Button>
              <Form.Submit loading={isSubmitting}>
                {isResponse ? 'Create Response' : 'Create Review'}
              </Form.Submit>
            </Group>
          </Form.Container>
        )}
      </Form>
    </BasicPage>
  );
};

export const ReviewUpdate: FC = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {jobId, id} = useTypedParams(ROUTES.ADMIN.JOB_REVIEWS.DETAIL);
  const [createOpen, setCreateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>();

  const {isJobReadonly} = useJobStore((state) => ({
    isJobReadonly: state.isJobReadonly,
  }));

  const breadcrumbs: BreadcrumbPart[] = useMemo(
    () => [
      {title: 'Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
      {
        title: `${jobId}`,
        href: ROUTES.ADMIN.JOBS.DETAIL.buildPath({id: jobId}),
      },
      {
        title: 'Reviews',
        href: ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId}),
      },
      {title: `${id}`},
    ],
    [id, jobId],
  );

  const fetchReview = useAsync(async () => {
    const {data} = await ReviewsService.getById({
      id,
    });
    return data;
  }, [id]);

  const review = fetchReview.value || undefined;

  const onSubmit = useCallback<SubmitHandler<ReviewSchema>>(
    async (value) => {
      if (value.createDocumentRequest && !value.createDocumentRequest.name)
        value.createDocumentRequest.name = extractFileName(
          value.createDocumentRequest.url,
        );
      const response = await ReviewsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Review Updated');
      navigate(ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId}));
    },
    [id, jobId, navigate, notifications],
  );

  const [deleteReviewState, deleteReview] = useAsyncFn(async () => {
    const response = await ReviewsService.deleteById({id});

    if (response.hasErrors) {
      notifications.error('Unable to delete review');
      return;
    }

    notifications.success('Review deleted');
    navigate(ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId}));
  }, [id, jobId, navigate, notifications]);

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchReview}>
        <Stack gap='xl'>
          <Paper withBorder shadow='sm'>
            <Form
              schema={reviewSchema}
              onSubmit={onSubmit}
              defaultValues={review}
            >
              <Form.Container withBorder={false} shadow='none'>
                <FormFields isUpdate isReadonly={isJobReadonly} />
                <Text fz={14} mt={16} fw={600}>
                  Documents
                </Text>
                <ReviewDocumentListing
                  setCreateOpen={setCreateOpen}
                  setUpdateOpen={setUpdateOpen}
                  setDocumentId={setDocumentId}
                />
                <Group mt={24} justify='space-between'>
                  {!isJobReadonly && (
                    <DeleteButton
                      onConfirm={deleteReview}
                      buttonLabel='Delete Review'
                      confirmButtonLabel='Delete Review'
                      loading={deleteReviewState.loading}
                      confirmTitle={
                        <Text fw={600}>
                          Are you sure you want to delete this review?
                        </Text>
                      }
                    />
                  )}
                  <Group>
                    <Button
                      type='button'
                      variant='outline'
                      component={Link}
                      to={ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId})}
                    >
                      Cancel
                    </Button>
                    {!isJobReadonly && <Form.Submit>Update Review</Form.Submit>}
                  </Group>
                </Group>
              </Form.Container>
            </Form>
            <DocumentCreate
              reviewId={id}
              jobId={jobId}
              open={createOpen}
              setOpen={setCreateOpen}
            />
            <DocumentUpdate
              open={updateOpen}
              setOpen={setUpdateOpen}
              id={documentId}
              jobId={jobId}
            />
          </Paper>
        </Stack>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields: FC<ReviewProps> = ({
  isResponse,
  isUpdate,
  errors,
  jobId,
  isReadonly,
}) => {
  const notifications = useNotification();
  const codeOptions =
    useOptions(CodesService.getDocumentTypeCodeOptions).value ?? [];

  const {watch, setError, setValue} = useFormContext<ReviewSchema>();

  useEffect(() => {
    if (errors) {
      errors.forEach((e) => {
        setError(e.propertyName as FieldPath<ReviewSchema>, {
          message: e.errorMessage,
        });
      });
    }
  }, [errors, setError]);

  const watchFileUpload = watch('createDocumentRequest.url');

  const renderDocumentFields = useMemo(
    () => watchFileUpload && watchFileUpload !== '',
    [watchFileUpload],
  );

  const onDrop = useCallback(
    async (file: File | null) => {
      if (!file) {
        return;
      }

      const response = await FilesService.upload({file});

      if (response.hasErrors) {
        notifications.errors(response.errors);
        return;
      }

      return response.data?.uri;
    },
    [notifications],
  );

  const onFileClear = useCallback(() => {
    setValue('createDocumentRequest', {
      jobId: jobId!,
      codeId: 0,
      reviewId: 0,
      name: '',
      url: '',
    });
  }, [jobId, setValue]);

  return (
    <Stack gap='xs'>
      {isResponse && (
        <Form.Select
          label='Response Type'
          name='status'
          enumObject={ReviewStatuses}
          disabled={isReadonly}
        />
      )}
      <Form.TextArea label='Message' name='message' disabled={isReadonly} />
      {!isUpdate && (
        <>
          {renderDocumentFields && (
            <>
              <Form.TextInput
                name='createDocumentRequest.name'
                label='File Name'
                disabled={isReadonly}
              />
              <Form.Select
                name='createDocumentRequest.codeId'
                label='File Type'
                data={codeOptions}
                disabled={isReadonly}
              />
            </>
          )}
          <Form.FileButton
            name='createDocumentRequest.url'
            onChange={onDrop}
            onFileClear={onFileClear}
            disabled={isReadonly}
          />
        </>
      )}
    </Stack>
  );
};
